import { useEffect, useRef } from "react";

import { injectIntl, intlFormatPropTypes } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { hot } from "hot";

import { HostProfileMain } from "../../app-host/HostProfileMain";
import { setNavStep } from "../actions/nav";
import { fetchSpace } from "../actions/space";
import { selectAuthAccessToken } from "../selectors/auth.selectors";
import { selectProfileIsFetching } from "../selectors/profile.selectors";
import {
  selectSpaceCompletionPercentage,
  selectSpaceIsFetching,
} from "../selectors/space.selectors";

// The store is still plain JS, so there's no canonical `AppDispatch` to import.
// `fetchSpace` and `setNavStep` are thunks, which untyped `useDispatch` rejects.
type Dispatch = (action: unknown) => Promise<unknown>;

// Everything this container does is feed `HostProfileMain`, so its own props
// are that component's contract minus the state it now reads from the store.
export type HostingProfileContainerProps = {
  intl: intlFormatPropTypes;
  params: { spaceId: string };
};

const HostingProfileContainerComponent = ({
  intl,
  params,
}: HostingProfileContainerProps) => {
  const dispatch = useDispatch() as Dispatch;
  const accessToken = useSelector(selectAuthAccessToken);

  const { spaceId } = params;
  const isFetchingSpace = useSelector(selectSpaceIsFetching);
  const isFetchingProfile = useSelector(selectProfileIsFetching);
  const progressBarWidth = useSelector(selectSpaceCompletionPercentage);
  // The nav step is set on the fetch's falling edge, so the first render has
  // nothing to compare against and must not dispatch.
  const wasFetchingSpace = useRef(isFetchingSpace);

  useEffect(() => {
    dispatch(fetchSpace(spaceId, accessToken));
  }, [accessToken, dispatch, spaceId]);

  useEffect(() => {
    if (wasFetchingSpace.current && !isFetchingSpace) {
      dispatch(
        setNavStep(
          intl.formatMessage({
            id: "hosting.host_profile_container.your_profile",
            defaultMessage: "Your profile",
          }),
          progressBarWidth,
        ),
      );
    }

    wasFetchingSpace.current = isFetchingSpace;
  }, [dispatch, intl, isFetchingSpace, progressBarWidth]);

  return (
    <div>
      {isFetchingProfile ? null : <HostProfileMain routeParams={{ spaceId }} />}
    </div>
  );
};

// `hot` resolves to `hot-reloading/production-hot.js` (an identity wrapper) for
// every Vite build, and that implementation takes no arguments — the `module`
// argument the .jsx containers still pass is a webpack/react-hot-loader leftover.
export default hot()(injectIntl(HostingProfileContainerComponent));
